import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { applyTheme } from '../theme/presets';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) { setLoading(false); return; }
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Re-apply theme whenever user (and therefore user.settings.theme) changes
  useEffect(() => {
    applyTheme(user?.settings?.theme);
  }, [user?.settings?.theme]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    return data;
  };

  const requestOTP = async (email) => {
    const { data } = await api.post('/auth/request-otp', { email });
    return data;
  };

  const verifyOTP = async (email, code, flow) => {
    const { data } = await api.post('/auth/verify-otp', { email, code, flow });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    return data;
  };

  const setPassword = async (newPassword) => {
    await api.post('/auth/set-password', { newPassword });
    setUser(prev => ({ ...prev, isFirstLogin: false, mustResetPassword: false }));
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, requestOTP, verifyOTP, setPassword, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
